<template>
  <div class="button-link-element-wrapper">
    <div class="element-header">
      <span class="element-type-badge button-link-badge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="8.5" cy="8.5" r="1.5"></circle>
          <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
        <span>Carte avec bouton</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>
    
    <div class="button-link-content">
      <!-- Image Header Section -->
      <div class="image-header-section">
        <label class="section-label">Image header</label>
        <div class="image-upload-area" @click="uploadImage">
          <div v-if="element.image_header || localImageUrl" class="image-container">
            <img :src="localImageUrl || element.image_header" alt="Header Image" class="header-image" />
            <div class="image-overlay">
              <button class="change-image-btn" title="Changer l'image" :disabled="isUploading">
                <svg v-if="!isUploading" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                  <circle cx="12" cy="13" r="4"></circle>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="uploading-spinner">
                  <circle cx="12" cy="12" r="10" opacity="0.3"></circle>
                  <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
                </svg>
              </button>
            </div>
          </div>
          <div v-else class="image-placeholder" :class="{ 'uploading': isUploading }">
            <svg v-if="!isUploading" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
            <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="uploading-spinner">
              <circle cx="12" cy="12" r="10" opacity="0.3"></circle>
              <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
            </svg>
            <p v-if="!isUploading">Cliquez pour ajouter une image</p>
            <p v-else>Upload en cours...</p>
          </div>
        </div>
      </div>

      <!-- Body Message Section -->
      <div class="text-section">
        <label class="section-label">Body text</label>
        <div class="text-input-container">
          <textarea 
            v-model="localBodyMessage"
            @blur="updateBodyMessage"
            @input="handleInput"
            placeholder="Entrez le texte principal..."
            rows="3"
            maxlength="320"
            class="text-input"
          ></textarea>
          <span class="char-count" :class="{'char-count-limit': localBodyMessage.length >= 320}">{{ localBodyMessage.length }}/320</span>
        </div>
      </div>

      <!-- Footer Message Section -->
      <div class="text-section">
        <label class="section-label">Footer text</label>
        <div class="text-input-container">
          <textarea 
            v-model="localFooterMessage"
            @blur="updateFooterMessage"
            @input="handleInput"
            placeholder="Entrez le texte de pied..."
            rows="2"
            maxlength="160"
            class="text-input"
          ></textarea>
          <span class="char-count" :class="{'char-count-limit': localFooterMessage.length >= 160}">{{ localFooterMessage.length }}/160</span>
        </div>
      </div>

      <!-- Button Action Section -->
      <div class="button-section">
        <label class="section-label">CTA URL button</label>
        <div class="button-inputs">
          <div class="input-group">
            <label class="input-label">Texte du bouton</label>
            <input 
              type="text" 
              v-model="localButtonText"
              @blur="updateButtonText"
              placeholder="Texte du bouton"
              maxlength="20"
              class="button-text-input"
            />
            <span class="char-count small">{{ localButtonText.length }}/20</span>
          </div>
          <div class="input-group">
            <label class="input-label">URL de destination</label>
            <input 
              type="url" 
              v-model="localButtonUrl"
              @blur="updateButtonUrl"
              placeholder="https://example.com"
              class="button-url-input"
            />
          </div>
        </div>
      </div>

      <!-- Preview Section -->
      <div class="preview-section">
        <label class="section-label">Aperçu</label>
        <div class="message-preview">
          <div class="preview-card">
            <div v-if="element.image_header || localImageUrl" class="preview-header">
              <img :src="localImageUrl || element.image_header" alt="Header" />
            </div>
            <div class="preview-content">
              <p class="preview-body" v-if="localBodyMessage">{{ localBodyMessage }}</p>
              <p class="preview-body placeholder" v-else>Body text</p>
              
              <p class="preview-footer" v-if="localFooterMessage">{{ localFooterMessage }}</p>
              <p class="preview-footer placeholder" v-else>Footer text</p>
              
              <div class="preview-time">{{ formattedTime }}</div>
              
              <button class="preview-button" disabled>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
                {{ localButtonText || 'Button text' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { axiosPeelo } from '@/utils/axios'
import { apiBaseUrl } from '@/config'
import { consoleError } from '@/utils/logger'

export default {
  name: 'ButtonLinkElement',
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isUploading: false,
      localImageUrl: null,
      localBodyMessage: '',
      localFooterMessage: '',
      localButtonText: '',
      localButtonUrl: ''
    }
  },
  computed: {
    formattedTime() {
      return new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    }
  },
  mounted() {
    // Initialiser les valeurs locales
    this.localBodyMessage = this.element.body_message || '';
    this.localFooterMessage = this.element.footer_message || '';
    this.localButtonText = this.element.action?.parameters?.display_text || '';
    this.localButtonUrl = this.element.action?.parameters?.url || '';
  },
  watch: {
    // Mettre à jour les valeurs locales si l'élément change
    'element.body_message'(newVal) {
      this.localBodyMessage = newVal || '';
    },
    'element.footer_message'(newVal) {
      this.localFooterMessage = newVal || '';
    },
    'element.action.parameters.display_text'(newVal) {
      this.localButtonText = newVal || '';
    },
    'element.action.parameters.url'(newVal) {
      this.localButtonUrl = newVal || '';
    }
  },
  methods: {
    handleInput() {
      // Ne fait rien - on sauvegarde seulement au blur
    },
    
    updateBodyMessage() {
      if (this.localBodyMessage !== (this.element.body_message || '')) {
        this.updateElement({ body_message: this.localBodyMessage });
      }
    },
    
    updateFooterMessage() {
      if (this.localFooterMessage !== (this.element.footer_message || '')) {
        this.updateElement({ footer_message: this.localFooterMessage });
      }
    },
    
    updateButtonText() {
      if (this.localButtonText !== (this.element.action?.parameters?.display_text || '')) {
        const updatedAction = {
          ...this.element.action,
          parameters: {
            ...this.element.action.parameters,
            display_text: this.localButtonText
          }
        };
        this.updateElement({ action: updatedAction });
      }
    },
    
    updateButtonUrl() {
      if (this.localButtonUrl !== (this.element.action?.parameters?.url || '')) {
        const updatedAction = {
          ...this.element.action,
          parameters: {
            ...this.element.action.parameters,
            url: this.localButtonUrl
          }
        };
        this.updateElement({ action: updatedAction });
      }
    },
    
    updateElement(changes) {
      const updatedElement = { ...this.element, ...changes };
      this.$emit('update', updatedElement);
    },

    // Méthode pour déclencher l'upload d'image
    uploadImage() {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.png,.jpg,.jpeg,image/png,image/jpeg,image/jpg';
      fileInput.style.display = 'none';
      
      fileInput.addEventListener('change', (event) => {
        this.handleImageUpload(event);
      });
      
      document.body.appendChild(fileInput);
      fileInput.click();
      setTimeout(() => {
        document.body.removeChild(fileInput);
      }, 1000);
    },
    
    // Méthode pour gérer l'upload d'image (inspirée d'ImageElement.vue)
    async handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Vérifications similaires à ImageElement.vue
      const fileName = file.name.toLowerCase();
      const isCSV = fileName.endsWith('.csv') || 
                    file.type === 'text/csv' || 
                    file.type === 'application/csv' ||
                    file.type === 'text/comma-separated-values';
      
      if (isCSV) {
        this.$emit('notification', {
          message: "Les fichiers CSV ne sont pas autorisés. Veuillez sélectionner une image (JPG, PNG, GIF, etc.)",
          type: "error"
        });
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const allowedExtensions = ['.jpg', '.jpeg', '.png'];
      
      const isValidType = allowedTypes.includes(file.type);
      const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      if (!isValidType || !isValidExtension) {
        this.$emit('notification', {
          message: "Format non autorisé. Seuls les fichiers PNG, JPG et JPEG sont acceptés.",
          type: "error"
        });
        return;
      }
      
      const maxSizeInBytes = 5 * 1024 * 1024; // 5 Mo
      if (file.size > maxSizeInBytes) {
        const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
        this.$emit('notification', {
          message: `L'image est trop volumineuse (${fileSizeInMB} Mo). Taille maximum autorisée : 5 Mo`,
          type: "error"
        });
        return;
      }
      
      this.isUploading = true;
      this.localImageUrl = URL.createObjectURL(file);
       
      try {
        const formData = new FormData();
        formData.append('imagefromplayground', file);
        
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Session expirée, veuillez vous reconnecter');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        };
        
        const response = await axiosPeelo.post(
          '/strong/upload/image/block',
          formData,
          config
        );
          
        if (response.data && response.data.success) {
          const imageUrl = `${apiBaseUrl}/${response.data.filePath}`;
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          const updatedElement = { ...this.element, image_header: imageUrl };
          this.$emit('update', updatedElement);
        } else {
          throw new Error(response.data?.message || 'Erreur lors de l\'upload');
        }
        
      } catch (error) {
        consoleError('❌ Erreur lors de l\'upload de l\'image:', error);
        
        let errorMessage = 'Erreur lors de l\'upload de l\'image';
        if (error.response?.status === 401) {
          errorMessage = 'Session expirée, veuillez vous reconnecter';
        } else if (error.response?.status === 413) {
          errorMessage = 'L\'image est trop volumineuse';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.$emit('notification', {
          message: errorMessage,
          type: "error"
        });
        
        this.localImageUrl = null;
      } finally {
        this.isUploading = false;
      }
    }
  }
};
</script>

<style scoped>
.button-link-element-wrapper {
  margin-bottom: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  overflow: hidden;
  transition: all 0.2s ease;
}

.button-link-element-wrapper:hover {
  border-color: #cbd5e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
}

.button-link-badge {
  color: #3182ce;
}

.button-link-badge svg {
  color: #3182ce;
}

.element-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  background: transparent;
  border: none;
  color: #718096;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: #e2e8f0;
  color: #4a5568;
}

.delete-btn:hover {
  background: #fed7d7;
  color: #e53e3e;
}

.button-link-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 8px;
}

/* Image Header Section */
.image-upload-area {
  cursor: pointer;
  border: 2px dashed #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  transition: border-color 0.2s ease;
}

.image-upload-area:hover {
  border-color: #cbd5e0;
}

.image-container {
  position: relative;
  width: 100%;
  height: 120px;
}

.header-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.image-container:hover .image-overlay {
  opacity: 1;
}

.change-image-btn {
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #4a5568;
}

.image-placeholder {
  height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
  background: #f7fafc;
}

.image-placeholder p {
  margin: 8px 0 0 0;
  font-size: 14px;
}

/* Text Sections */
.text-input-container {
  position: relative;
}

.text-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  font-family: inherit;
}

.text-input:focus {
  outline: none;
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
}

.char-count {
  position: absolute;
  bottom: 4px;
  right: 8px;
  font-size: 11px;
  color: #718096;
  background: white;
  padding: 2px 4px;
}

.char-count-limit {
  color: #e53e3e;
  font-weight: 500;
}

/* Button Section */
.button-inputs {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.input-group {
  position: relative;
}

.input-label {
  display: block;
  font-size: 12px;
  color: #718096;
  margin-bottom: 4px;
}

.button-text-input,
.button-url-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 14px;
}

.button-text-input:focus,
.button-url-input:focus {
  outline: none;
  border-color: #3182ce;
  box-shadow: 0 0 0 2px rgba(49, 130, 206, 0.1);
}

.char-count.small {
  position: static;
  font-size: 10px;
  color: #a0aec0;
  margin-top: 2px;
}

/* Preview Section */
.message-preview {
  background: #f7fafc;
  border-radius: 6px;
  padding: 16px;
}

.preview-card {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  max-width: 280px;
}

.preview-header {
  height: 100px;
  overflow: hidden;
}

.preview-header img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-content {
  padding: 12px;
}

.preview-body {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #2d3748;
  line-height: 1.4;
}

.preview-footer {
  margin: 0 0 8px 0;
  font-size: 12px;
  color: #718096;
  line-height: 1.3;
}

.preview-body.placeholder,
.preview-footer.placeholder {
  color: #a0aec0;
  font-style: italic;
}

.preview-time {
  font-size: 11px;
  color: #a0aec0;
  margin-bottom: 12px;
}

.preview-button {
  background: #22c55e;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: not-allowed;
  opacity: 0.8;
  display: flex;
  align-items: center;
  gap: 6px;
}

.preview-button svg {
  width: 12px;
  height: 12px;
}

.uploading-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style> 